import { useEffect, useRef, useState } from "react";

import ThumbImg from "../../assets/images/conversations/047.webp";
import PlayIcon  from "../../assets/icons/conversations/play-icon.svg";

/* ─────────────────────────────────────────────
   PREFIX: em-
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Roboto:wght@400;500&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .em-page {
    background: #1A1A1A;
    font-family: 'Roboto', sans-serif;
    color: #fff;
  }
  .em-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
  }

  /* ══════════════════════════════════════
     SECTION 1 — HERO
  ══════════════════════════════════════ */
  .em-hero { padding-top: 60px; padding-bottom: 60px; }
  .em-hero__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: start;
  }
  .em-hero__left { display: flex; flex-direction: column; gap: 44px; }
  .em-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .em-hero__title.em-visible { opacity: 1; transform: translateY(0); }
  .em-hero__spotify {
    border-radius: 12px;
    overflow: hidden;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.18s, transform 0.7s ease 0.18s;
  }
  .em-hero__spotify.em-visible { opacity: 1; transform: translateY(0); }
  .em-hero__spotify iframe {
    display: block; width: 100%; height: 152px; border: none; border-radius: 12px;
  }
  .em-hero__right {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s;
  }
  .em-hero__right.em-visible { opacity: 1; transform: translateY(0); }

  /* thumbnail */
  .em-yt-thumb {
    position: relative; width: 100%; border-radius: 12px;
    overflow: hidden; cursor: pointer; display: block; background: #000;
  }
  .em-yt-thumb img {
    width: 100%; height: auto; display: block;
    border-radius: 12px; transition: opacity 0.3s ease;
  }
  .em-yt-thumb:hover img { opacity: 0.85; }
  .em-yt-thumb__play {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: none; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
  }
  .em-yt-thumb__play img { width: 40px; height: 44px; }
  .em-yt-iframe-wrap {
    position: relative; width: 100%; padding-bottom: 56.25%;
    height: 0; border-radius: 12px; overflow: hidden;
  }
  .em-yt-iframe-wrap iframe {
    position: absolute; top: 0; left: 0;
    width: 100%; height: 100%; border: none;
  }

  /* ══════════════════════════════════════
     SECTION 2 — BODY
  ══════════════════════════════════════ */
  .em-body {
    padding-bottom: 100px; opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .em-body.em-visible { opacity: 1; transform: translateY(0); }
  .em-body__para {
    font-family: 'Roboto', sans-serif; font-size: 16px;
    font-weight: 400; color: #E2E2E2; line-height: 160%; margin-bottom: 14px;
  }
  .em-body__para:last-child { margin-bottom: 0; }

  /* ── TABLET (max 768px) ── */
  @media (max-width: 768px) {
    .em-inner { padding-left: 32px; padding-right: 32px; }
    .em-hero { padding-top: 32px; padding-bottom: 48px; }
    .em-hero__grid { grid-template-columns: 1fr 1fr; gap: 24px; }
    .em-hero__title { font-size: 24px; }
    .em-hero__left { gap: 16px; }
    .em-body__para { font-size: 14px; margin-bottom: 12px; }
    .em-body { padding-bottom: 48px; }
    .em-yt-thumb__play img { width: 30px; height: 32px; }
  }

  /* ── MOBILE (max 480px) ── */
  @media (max-width: 480px) {
    .em-inner { padding-left: 16px; padding-right: 16px; }
    .em-hero { padding-top: 51px; padding-bottom: 30px; }
    .em-hero__grid { grid-template-columns: 1fr; gap: 30px; }
    .em-hero__left { gap: 30px; }
    .em-hero__title { font-size: 30px; }
    .em-body__para { font-size: 16px; margin-bottom: 10px; }
    .em-body { padding-bottom: 30px; }
    .em-yt-thumb__play img { width: 30px; height: 32px; }
  }
`;

function useFadeUp(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("em-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
}

const YT_VIDEO_ID = "_ZdNKPBVBUc";

function YouTubeEmbed() {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="em-yt-iframe-wrap">
        <iframe
          src={`https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1&rel=0`}
          title="EP 47 EMotorad"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div className="em-yt-thumb" onClick={() => setPlaying(true)} role="button" aria-label="Play video">
      <img src={ThumbImg} alt="EP 47 EMotorad thumbnail" />
      <div className="em-yt-thumb__play">
        <img src={PlayIcon} alt="Play" />
      </div>
    </div>
  );
}

function HeroSection() {
  const titleRef   = useRef(null);
  const spotifyRef = useRef(null);
  const rightRef   = useRef(null);
  useFadeUp(titleRef);
  useFadeUp(spotifyRef);
  useFadeUp(rightRef);

  return (
    <section className="em-hero">
      <div className="em-inner">
        <div className="em-hero__grid">
          <div className="em-hero__left">
            <h1 ref={titleRef} className="em-hero__title">
              EP 47 | Can electric cycles become a part of our everyday lives? | EMotorad
            </h1>
            <div ref={spotifyRef} className="em-hero__spotify">
              <iframe
                src="https://open.spotify.com/embed/episode/5IJG6IYJpdEdmR8PfBX9QK?utm_source=generator&theme=0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="EP 47 Spotify Player"
              />
            </div>
          </div>
          <div ref={rightRef} className="em-hero__right">
            <YouTubeEmbed />
          </div>
        </div>
      </div>
    </section>
  );
}

function BodySection() {
  const ref = useRef(null);
  useFadeUp(ref);
  return (
    <section ref={ref} className="em-body">
      <div className="em-inner">
        <p className="em-body__para">
          Bicycles have been pivotal in revolutionizing transportation, offering efficient and accessible mobility for centuries. From enabling faster commutes to fostering leisurely exploration, bicycles have shaped modern society's connectivity and sustainability. But, in this age of electrification, can bicycles still be a part of our everyday lives? Today we have Aditya Oza and Sumedh Battewar, the co-founders of Emotorad, a very interesting startup based out of Pune, who are working on making this a reality.
        </p>
      </div>
    </section>
  );
}

export default function Ep47EMotorad() {
  return (
    <>
      <style>{CSS}</style>
      <div className="em-page">
        <HeroSection />
        <BodySection />
      </div>
    </>
  );
}